import type { Geometry } from 'geojson';

// Backend enum: 'verified' / 'non_verified' / 'unreviewed'. Stays a string-id
// union (not a boolean) because 'unreviewed' is distinct from missing data —
// `null`/`undefined` means the field wasn't returned at all.
export type AfeVerificationState = 'verified' | 'non_verified' | 'unreviewed';

/** Entity associated with an AFE (sender or receiver). */
export interface AfeEntity {
  id: number;
  name: string;
  role: 'sender' | 'receiver';
}

/** File attached to an AFE record. */
export interface AfeSupportingFile {
  id: number;
  fileName: string;
  downloadUrl: string;
}

/** Well data nested in an AFE result. */
export interface AfeWell {
  type: string;
  slotId: string;
  name: string;
  href: string;
}

/** Provenance metadata for a Catalogue record that was created via Auto-Connect. */
export interface AfeSourceDocument {
  id: number;
  documentTitle: string | null;
  href: string;
  /** Direct link to the source AFE document's file blob (PDF). Shared with
   * the underlying Document::Afe — no duplicate SupportingFile row. */
  fileUrl: string | null;
  autoConnectedAt: string | null;
}

/** Single AFE result returned by the AFE Catalog query. */
export interface AfeCatalogResult {
  id: number;
  afeType: string;
  well: AfeWell;
  operator: string;
  operatorHref?: string | null;
  basinName: string;
  state: string;
  countyName: string;
  reportedFormation: string;
  afeDate: string;
  createdAt: string;
  heldFlag: string | null;
  status: string;
  entities: AfeEntity[];
  lateralLength: string | null;
  tvd: string | null;
  totalCost: number | null;
  drillingCost: number | null;
  completionCost: number | null;
  facilitiesCost: number | null;
  contingenciesCost: number | null;
  otherCost: number | null;
  verificationState?: AfeVerificationState | null;
  supportingFiles: AfeSupportingFile[];
  afeNumber: string | null;
  sourceDocument: AfeSourceDocument | null;
}

/** Flattened AFE row with nested well, entities, and files resolved to flat fields. */
export interface FlatAfeRow extends Omit<
  AfeCatalogResult,
  'well' | 'lateralLength' | 'tvd'
> {
  wellType: string;
  slotId: string;
  wellName: string;
  wellHref: string;
  lateralLength: number | null;
  tvd: number | null;
  senders: AfeEntity[];
  receivers: AfeEntity[];
}

/** Aggregated summary metrics returned alongside AFE Catalog results. */
export interface AfeCatalogSummary {
  totalCount: number;
  avgAfeCost: number | null;
  avgDrillingCost: number | null;
  avgCompletionCost: number | null;
  avgFacilitiesCost: number | null;
  avgContingenciesCost: number | null;
}

/** Maps well_type enum values to display labels. */
export const WELL_TYPE_LABELS: Record<string, string> = {
  existing_well: 'Existing Well',
  generic_well: 'Imported Well',
};

/** Maps afe_type enum values to display labels. */
export const AFE_TYPE_LABELS: Record<string, string> = {
  dc_and_e: 'Drilling, Completion & Equipping (DC&E)',
  facility: 'Facility',
  recompletion: 'Recompletion',
  p_and_a: 'Plug & Abandonment (P&A)',
  workover: 'Workover',
  other: 'Other',
};

/** Well information section in AFE detail response. */
export interface AfeDetailWellInformation {
  wellName: string | null;
  unit: string | null;
  unitHref: string | null;
  basinName: string | null;
  countyName: string | null;
  state: string | null;
  reportedFormation: string | null;
  drilledLateralLength: number | null;
  trueVerticalDepth: number | null;
  geometry: Geometry | null;
  plannedLateralLength: number | null;
  plannedTvd: number | null;
}

/** Derived well lookup fields used by AFE detail page. */
export interface AfeDetailWellLookup {
  wellName: string | null;
  operator: string | null;
  unit: string | null;
  unitHref: string | null;
  drilledLateralLength: number | null;
  trueVerticalDepth: number | null;
  geometry: Geometry | null;
}

/** Detailed entity entry in AFE detail response. */
export interface AfeDetailEntity {
  id: number;
  entityId: number;
  href: string;
  name: string;
  role: string;
  address: string | null;
  entityType: string | null;
}

/** Line item under an AFE scenario or cost summary. */
export interface AfeDetailCostItem {
  id: number;
  name: string;
  category: string;
  subCategory: string | null;
  unit: string;
  costPerUnit: number;
  quantity: number;
  lineTotal: number;
}

/** Cost totals grouped by top-level categories. */
export interface AfeDetailTotals {
  totalAfeCost: number;
  drillingCost: number;
  completionCost: number;
  facilitiesCost: number;
  contingenciesCost: number;
  otherCost: number;
}

/** Scenario payload in AFE detail response. */
export interface AfeDetailScenario {
  id: number;
  href: string;
  name: string;
  isActive: boolean;
  lateralLength: number | null;
  tvd: number | null;
  daysOnWell?: number | null;
  daysToDrill?: number | null;
  stageLength?: number | null;
  proppantIntensity?: number | null;
  fluidIntensity?: number | null;
  costItems: AfeDetailCostItem[];
  totals: AfeDetailTotals;
}

/** Aggregated cost summary section in AFE detail response. */
export interface AfeDetailCostSummary {
  lineItems: AfeDetailCostItem[];
  totals: AfeDetailTotals;
}

/** Supporting file entry in AFE detail response. */
export interface AfeDetailSupportingFile {
  id: number;
  fileName: string;
  href: string;
  attachmentHref: string | null;
  byteSize: number | null;
  createdBy: string | null;
  createdAt: string | null;
  downloadUrl: string | null;
}

/** Single embedded verification slot returned with an AFE detail. */
export interface AfeDetailVerificationSlot {
  status: boolean | null;
  id: number;
  commentsCount: number;
  href: {
    events: string;
    comments: string;
  };
}

/** Verification slots keyed by slot name (e.g. "afe"). */
export interface AfeDetailVerifications {
  afe?: AfeDetailVerificationSlot;
  [slot: string]: AfeDetailVerificationSlot | undefined;
}

/** Metadata about the most recent verification change. */
export interface AfeDetailVerificationMeta {
  changedAt: string | null;
  changedByName: string | null;
}

/** Top-level AFE detail record from GET /afe_headers/:id. */
export interface AfeHeaderDetail {
  id: number;
  afeType: string;
  afeTypeLabel: string | null;
  wellType: string;
  wellTypeLabel: string | null;
  slotId: string | null;
  slotHref: string | null;
  stateId: number | null;
  countyId: number | null;
  wellInformation: AfeDetailWellInformation;
  wellLookup: AfeDetailWellLookup;
  operator: string | null;
  operatorHref: string | null;
  currentWellOperator: string | null;
  currentWellOperatorHref: string | null;
  heldFlag: string | null;
  heldFlagLabel: string | null;
  status: string;
  statusLabel: string | null;
  entities: AfeDetailEntity[];
  afeScenarios: AfeDetailScenario[];
  costSummary: AfeDetailCostSummary;
  supportingFilesHref: string;
  supportingFiles: AfeDetailSupportingFile[];
  notes: string | null;
  afeNumber: string | null;
  afeDate: string | null;
  sourceDocument: AfeSourceDocument | null;
  verifications: AfeDetailVerifications;
  verificationMeta: AfeDetailVerificationMeta | null;
  createdAt: string;
  updatedAt: string;
}

/** Response wrapper for GET /afe_headers/:id endpoint. */
export interface AfeHeaderDetailResponse {
  data: AfeHeaderDetail;
}
